import React, { useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  FormSection,
  IconButton,
} from "@narmi/design_system";
import { useNotificationContext } from "cerulean";

import { DialogProps, DisplayTokenDialogProps } from "./types";

export const DisplayOnceTokenDialog = ({
  isOpen,
  onUserDismiss,
  createdToken,
}: DisplayTokenDialogProps) => {
  if (!createdToken) {
    return null;
  }

  const { sendNotification } = useNotificationContext();

  const copyText = async (text: string) => {
    await navigator.clipboard.writeText(text);
    sendNotification({
      type: "dismissible_success",
      text: "Text copied.",
    });
  };

  return (
    <Dialog
      title="Your API token"
      isOpen={isOpen}
      onUserDismiss={onUserDismiss}
      width="600px"
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button onClick={() => copyText(createdToken.token)}>
            Copy Token
          </Button>
        </div>
      }
    >
      <div style={{ fontSize: "var(--font-size-s)", margin: "10px 0" }}>
        <span>
          For security reasons, this is the only time we will display this token
          and header secret to you. You will need to generate a new token if you
          lose this one.
        </span>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          fontWeight: "bold",
          color: "var(--color-black)",
        }}
      >
        <FormSection title="Bearer Token" />
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            backgroundColor: "var(--bgColor-smokeGrey)",
            padding: "10px",
            width: "540px",
          }}
        >
          <span>{createdToken?.token}</span>
          <IconButton
            name="copy"
            onClick={() => copyText(createdToken?.token || "")}
          />
        </div>
        <FormSection title="Header Secret" />
        <div
          style={{
            display: "flex",
            backgroundColor: "var(--bgColor-smokeGrey)",
            padding: "10px",
            width: "540px",
          }}
        >
          <span
            style={{
              width: "500px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {createdToken?.secret}
          </span>
          <IconButton
            name="copy"
            onClick={() => copyText(createdToken?.secret || "")}
          />
        </div>
      </div>
    </Dialog>
  );
};

export const TermsOfUseDialog = ({
  isOpen,
  onUserDismiss,
  onConfirm,
}: DialogProps) => {
  const [hasAgreed, setHasAgreed] = useState(false);
  const [alertEl, setAlertEl] = useState<React.ReactNode>(null);

  const onConfirmInner = () => {
    setAlertEl(null);
    if (hasAgreed) {
      onConfirm();
    } else {
      setAlertEl(
        <Alert isActive kind="error">
          Must accept terms.
        </Alert>,
      );
    }
  };

  const dialogFooter = (
    <div style={{ display: "flex", justifyContent: "flex-end" }}>
      <Button
        style={{ paddingRight: "10px" }}
        kind="plain"
        onClick={onUserDismiss}
      >
        Cancel
      </Button>
      <Button onClick={onConfirmInner}>Confirm</Button>
    </div>
  );

  return (
    <Dialog
      title="Review terms of use"
      isOpen={isOpen}
      onUserDismiss={onUserDismiss}
      notification={alertEl}
      footer={dialogFooter}
    >
      <p>
        This Narmi Developer License and Terms (the “Agreement”) constitutes a
        binding contract by and between Narmi Inc. a Delaware corporation
        (“Narmi”), and you (“Developer”). This Agreement governs Developer’s use
        of the API and Narmi Materials. Narmi and Developer are referred to
        herein individually as a “Party”, and together, collectively, the
        “Parties”.
      </p>
      <p>
        BY ACCESSING OR USING THE TOOLS OR NARMI MATERIALS, YOU (A) ACKNOWLEDGE
        THAT YOU HAVE READ AND UNDERSTAND THIS AGREEMENT; (B) REPRESENT AND
        WARRANT THAT YOU HAVE THE RIGHT, POWER, AND AUTHORITY TO ENTER INTO THIS
        AGREEMENT; AND (C) ACCEPT THIS AGREEMENT AND AGREE THAT YOU ARE LEGALLY
        BOUND BY ITS TERMS. IF YOU DO NOT ACCEPT THESE TERMS, YOU MAY NOT ACCESS
        OR USE THE TOOLS OR NARMI MATERIALS.
      </p>
      <div style={{ display: "flex", justifyContent: "flex-start" }}>
        <Checkbox
          label="I accept the"
          name="agree"
          checked={hasAgreed}
          onChange={() => setHasAgreed(!hasAgreed)}
        />
        <span>&nbsp;</span>
        <a
          href="https://www.narmi.com/policies/developer-terms-conditions"
          target="_blank"
          rel="noopener noreferrer"
        >
          Terms of Use
        </a>
      </div>
    </Dialog>
  );
};
